import React, { ReactElement, useMemo } from 'react'
import { useRecoilState } from 'recoil'
import { selectedFeedbackNotificationAtom } from '@/atoms/notifications'
import { Modal, Button } from 'antd'
import { JEPNotificationType } from 'common/src/api/websiteFrontendVsWebsiteBackend/notifications/types'
import { buildHrefForFeedback } from '@/components/NotificationModal/NotificationListItem/NotificationListItem.types'
import { displayStringForCommentPostTime } from '@/utils/dateTime'
import Link from 'next/link'
import styles from './FeedbackDetailModal.module.less'

export const FeedbackDetailModal: React.FC = (): ReactElement => {
    const [selectedNotification, setSelectedNotification] = useRecoilState(
        selectedFeedbackNotificationAtom
    )

    const isOpen =
        selectedNotification?.type === JEPNotificationType.FEEDBACK ?? false
    const metadata =
        selectedNotification?.type === JEPNotificationType.FEEDBACK
            ? selectedNotification.metadata
            : null

    const contentHref = useMemo(() => {
        if (!metadata) return null
        if (metadata.contentPath && metadata.specificContentID) {
            return buildHrefForFeedback(metadata)
        }
        return null
    }, [metadata])

    const hasContentLink =
        contentHref !== null &&
        typeof contentHref === 'object' &&
        'query' in contentHref &&
        contentHref.query !== undefined

    return (
        <Modal
            title="Feedback Details"
            open={isOpen}
            onCancel={() => setSelectedNotification(null)}
            footer={null}
            width={600}
        >
            {metadata && (
                <div className={styles.feedbackDetailContent}>
                    <div className={styles.feedbackText}>
                        {metadata.feedback}
                    </div>
                    <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Email:</span>
                        <span className={styles.detailValue}>
                            {metadata.userEmail}
                        </span>
                    </div>
                    <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Course:</span>
                        <span className={styles.detailValue}>
                            {metadata.courseName}
                        </span>
                    </div>
                    <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Page:</span>
                        <span className={styles.detailValue}>
                            {metadata.page}
                        </span>
                    </div>
                    <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>
                            Support Ticket:
                        </span>
                        <span className={styles.detailValue}>
                            {metadata.supportTicketID}
                        </span>
                    </div>
                    {selectedNotification?.createdAt && (
                        <div className={styles.detailRow}>
                            <span className={styles.detailLabel}>
                                Submitted:
                            </span>
                            <span className={styles.detailValue}>
                                {displayStringForCommentPostTime(
                                    selectedNotification.createdAt
                                )}
                            </span>
                        </div>
                    )}
                    {metadata.relatedContentType && (
                        <div className={styles.detailRow}>
                            <span className={styles.detailLabel}>
                                Content Type:
                            </span>
                            <span className={styles.detailValue}>
                                {metadata.relatedContentType}
                            </span>
                        </div>
                    )}
                    {metadata.specificContentID && (
                        <div className={styles.detailRow}>
                            <span className={styles.detailLabel}>
                                Content ID:
                            </span>
                            <span className={styles.detailValue}>
                                {metadata.specificContentID}
                            </span>
                        </div>
                    )}
                    {hasContentLink && (
                        <div className={styles.contentLinkSection}>
                            <Link href={contentHref}>
                                <Button
                                    type="primary"
                                    onClick={() =>
                                        setSelectedNotification(null)
                                    }
                                >
                                    View Related Content
                                </Button>
                            </Link>
                        </div>
                    )}
                </div>
            )}
        </Modal>
    )
}
